/**
 * Create Club Step
 *
 * Club creation for users without club context (Path B1).
 * Issue #692 - Club-First Onboarding
 * Issue #721 - Enhanced with Nightfall Figma design patterns
 */

import { useState, type FormEvent } from 'react';
import { Users, ChevronLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import { clubsApi } from '@/lib/api/clubs';
import { US_STATES } from '@/constants/usStates';

interface CreateClubStepProps {
  onNext: () => void;
  onBack: () => void;
  onClubCreated: (clubSlug: string, clubName: string) => void;
  /**
   * Slug of a club already created in THIS onboarding session (#2693 re-entry
   * guard). The step unmounts/re-mounts fresh on Back from Activities, losing its
   * internal form state; the parent modal persists the created slug and passes it
   * back here. When set, render an "already created" confirmation instead of the
   * form — no second club can be created. Null until a club is created.
   */
  createdClubSlug?: string | null;
  /** Name of the club created this session, for the confirmation copy (#2693). */
  createdClubName?: string | null;
}

interface CreateClubFormData {
  name: string;
  city: string;
  state: string;
  primary_sport: string;
}

// PRIMARY_SPORTS list from backend/src/schemas/index.ts
const PRIMARY_SPORTS = [
  'Running',
  'Trail Running',
  'Cycling',
  'Mountain Biking',
  'Walking',
  'Hiking',
  'Swimming',
  'Triathlon',
  'Strength Training',
  'Yoga',
  'General Fitness',
  'Wheelchair',
  'Other'
] as const;

export default function CreateClubStep({ onNext, onBack, onClubCreated, createdClubSlug, createdClubName }: CreateClubStepProps) {
  const [wantsToCreate, setWantsToCreate] = useState(false); // Default: skip
  const [formData, setFormData] = useState<CreateClubFormData>({
    name: '',
    city: '',
    state: '',
    primary_sport: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation helper (Nightfall pattern) - Issue #641: city/state for discovery
  const formIsValid = formData.name.trim() && formData.city.trim() && formData.primary_sport;
  const canProceed = !wantsToCreate || formIsValid; // Can always proceed if skipping

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // If skipping, just proceed to next step
    if (!wantsToCreate) {
      onNext();
      return;
    }

    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!formData.name.trim()) {
        setError('Club name is required');
        setIsSubmitting(false);
        return;
      }

      if (!formData.city.trim()) {
        setError('City is required');
        setIsSubmitting(false);
        return;
      }

      if (!formData.primary_sport) {
        setError('Please select a primary sport');
        setIsSubmitting(false);
        return;
      }

      // Create club via existing API - Issue #641: city/state for discovery
      const response = await clubsApi.createClub({
        name: formData.name.trim(),
        city: formData.city.trim(),
        state: formData.state || undefined,
        location: formData.city.trim() + (formData.state ? `, ${formData.state}` : ''),
        primary_sport: formData.primary_sport
      });

      // Update onboarding context with new club slug (for navigation) + name
      // (for the #2693 re-entry confirmation on Back).
      onClubCreated(response.club.slug, formData.name.trim());

      // Proceed to next step
      onNext();

    } catch (err: unknown) {
      // Detailed error handling
      const error = err as { response?: { data?: { error?: { message?: string } }; status?: number }; message?: string };
      const errorData = error?.response?.data?.error;
      let errorMessage = 'Failed to create club. Please try again.';

      if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // Handle specific error cases
      if (error?.response?.status === 409) {
        errorMessage = 'A club with this name already exists. Please choose another name.';
      } else if (error?.response?.status === 400) {
        errorMessage = 'Please check your club information and try again.';
      }

      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof CreateClubFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  // #2693 re-entry guard: a club was already created this session (parent passes
  // the persisted slug back after this step re-mounts on Back). Render a
  // confirmation instead of the create form so no second club can be created —
  // Continue advances, Back returns to Profile.
  if (createdClubSlug) {
    return (
      <div className="flex flex-col flex-1 min-h-0">
        {/* Progress Bar - Step 3 of 6 */}
        <div className="shrink-0 px-6 pt-4">
          <div className="w-full h-1 bg-oro-surface-alt rounded-full overflow-hidden">
            <div className="h-full bg-oro-gold rounded-full transition-all duration-300" style={{ width: '50%' }} />
          </div>
          <p className="text-text-muted text-xs mt-2 text-center">Step 3 of 6</p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-oro-gold to-oro-gold-dark flex items-center justify-center shadow-lg mb-4 mx-auto">
              <CheckCircle2 className="w-8 h-8 text-black" />
            </div>
            <h2 id="onboarding-title" className="text-xl font-semibold text-text-primary mb-2">
              {createdClubName ? `You created ${createdClubName}` : 'Your club is ready'}
            </h2>
            <p className="text-text-muted text-sm">
              Your club is set up. Continue to finish your profile.
            </p>
          </div>
        </div>

        <div className="shrink-0 p-4 border-t border-oro-border bg-oro-surface">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-3 rounded-lg bg-oro-surface-alt border border-oro-border text-text-primary hover:bg-oro-surface-alt/80 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={onNext}
              className="flex-1 px-4 py-3 rounded-lg flex items-center justify-center gap-2 bg-oro-gold hover:bg-oro-gold/90 shadow-lg transition-all"
            >
              <span className="text-black font-medium">Continue</span>
              <ArrowRight className="w-5 h-5 text-black" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Progress Bar - Step 3 of 6 */}
      <div className="shrink-0 px-6 pt-4">
        <div className="w-full h-1 bg-oro-surface-alt rounded-full overflow-hidden">
          <div
            className="h-full bg-oro-gold rounded-full transition-all duration-300"
            style={{ width: '50%' }}
          />
        </div>
        <p className="text-text-muted text-xs mt-2 text-center">Step 3 of 6</p>
      </div>

      {/* Scrollable Content - min-h-0 allows shrinking in flex container (#831) */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
        {/* Icon Header (Nightfall pattern) */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-oro-gold to-oro-gold-dark flex items-center justify-center shadow-lg mb-4 mx-auto">
            <Users className="w-8 h-8 text-black" />
          </div>
          <h2 id="onboarding-title" className="text-xl font-semibold text-text-primary mb-2">
            Start a Club?
          </h2>
          <p className="text-text-muted text-sm">You can always create one later</p>
        </div>

        {/* Yes/No Toggle */}
        <div className="flex gap-3 mb-6">
          <button
            type="button"
            onClick={() => setWantsToCreate(true)}
            className={`flex-1 py-3 rounded-lg font-medium transition-all ${
              wantsToCreate
                ? 'bg-oro-gold text-black shadow-lg'
                : 'bg-oro-surface-alt border border-oro-border text-text-secondary hover:border-oro-border-hover'
            }`}
          >
            Yes, create a club
          </button>
          <button
            type="button"
            onClick={() => setWantsToCreate(false)}
            className={`flex-1 py-3 rounded-lg font-medium transition-all ${
              !wantsToCreate
                ? 'bg-oro-gold text-black shadow-lg'
                : 'bg-oro-surface-alt border border-oro-border text-text-secondary hover:border-oro-border-hover'
            }`}
          >
            No, skip for now
          </button>
        </div>

        {/* Form */}
        <form id="create-club-form" onSubmit={handleSubmit} className={`space-y-4 transition-opacity ${!wantsToCreate ? 'opacity-40 pointer-events-none' : ''}`}>
          {/* Club Name */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
            <label htmlFor="club-name" className="text-text-secondary text-sm">
              Club Name
            </label>
            <input
              id="club-name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Huntington Run Club"
              maxLength={100}
              className="w-full bg-oro-surface-alt border border-oro-border focus:border-oro-gold text-text-primary placeholder:text-text-muted rounded-lg px-4 py-3 outline-none transition-colors mt-2"
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          {/* Location - Issue #641: city/state for discovery */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
            <label className="text-text-secondary text-sm">
              Location
            </label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <input
                id="city"
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="City"
                maxLength={100}
                className="w-full bg-oro-surface-alt border border-oro-border focus:border-oro-gold text-text-primary placeholder:text-text-muted rounded-lg px-4 py-3 outline-none transition-colors"
                disabled={isSubmitting}
              />
              <select
                id="state"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                className="w-full bg-oro-surface-alt border border-oro-border focus:border-oro-gold text-text-primary rounded-lg px-4 py-3 outline-none transition-colors"
                disabled={isSubmitting}
              >
                <option value="">State</option>
                {US_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Primary Sport */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
            <label htmlFor="primary-sport" className="text-text-secondary text-sm">
              Primary Activity
            </label>
            <select
              id="primary-sport"
              value={formData.primary_sport}
              onChange={(e) => handleInputChange('primary_sport', e.target.value)}
              className="w-full bg-oro-surface-alt border border-oro-border focus:border-oro-gold text-text-primary rounded-lg px-4 py-3 outline-none transition-colors mt-2"
              disabled={isSubmitting}
            >
              <option value="">Choose one...</option>
              {PRIMARY_SPORTS.map((sport) => (
                <option key={sport} value={sport}>
                  {sport}
                </option>
              ))}
            </select>
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-4 bg-oro-error/10 border border-oro-error/20 rounded-xl space-y-2">
              <p className="text-sm text-oro-error">{error}</p>
              <button
                type="button"
                onClick={() => window.open('/clubs/create', '_blank')}
                className="text-xs text-oro-error/75 hover:text-oro-error/50 underline"
              >
                Open full create page
              </button>
            </div>
          )}
        </form>
      </div>

      {/* Bottom-Docked Actions - shrink-0 ensures visibility on small screens (#831) */}
      <div className="shrink-0 p-4 border-t border-oro-border bg-oro-surface">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={isSubmitting}
            className="px-4 py-3 rounded-lg bg-oro-surface-alt border border-oro-border text-text-primary hover:bg-oro-surface-alt/80 transition-colors disabled:opacity-50"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="submit"
            form="create-club-form"
            disabled={isSubmitting || !canProceed}
            className={`flex-1 px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-all ${
              canProceed && !isSubmitting
                ? 'bg-oro-gold hover:bg-oro-gold/90 shadow-lg'
                : 'bg-oro-surface-alt border border-oro-border text-text-muted cursor-not-allowed'
            }`}
          >
            <span className={canProceed && !isSubmitting ? 'text-black font-medium' : ''}>
              {isSubmitting ? 'Creating...' : wantsToCreate ? 'Create Club' : 'Continue'}
            </span>
            {!isSubmitting && <ArrowRight className={`w-5 h-5 ${canProceed ? 'text-black' : ''}`} />}
          </button>
        </div>
      </div>
    </div>
  );
}